import { getApiUrl } from "./locale";
import { PS_STORAGE_CHANGE_EVENT } from "./storage";

export type SocialProvider = "google" | "apple" | "linkedin";

export interface ProfileAuthSession {
	accessToken: string;
	isNewUser?: boolean;
	signedInAt: number;
}

interface PopupAuthMessage {
	type?: string;
	ok?: boolean;
	accessToken?: string;
	isNewUser?: boolean;
	error?: string;
}

const SESSION_KEY = "primestyle_profile_session";
const AUTH_MESSAGE_TYPE = "PRIMESTYLE_SDK_AUTH";
const POPUP_TIMEOUT_MS = 120_000;
const POLL_INTERVAL_MS = 900;

function emitProfileAuthChange(reason: string): void {
	if (typeof window === "undefined") return;
	try {
		window.dispatchEvent(new CustomEvent(PS_STORAGE_CHANGE_EVENT, { detail: { reason } }));
	} catch {
		/* dispatch may be blocked in old embedded browsers */
	}
}

function isTrustedAuthOrigin(eventOrigin: string, expectedOrigin: string): boolean {
	if (eventOrigin === expectedOrigin) return true;
	try {
		const host = new URL(eventOrigin).hostname;
		return host === "localhost"
			|| host === "127.0.0.1"
			|| host.endsWith(".primestyleai.com")
			|| host.endsWith(".myaifitting.com");
	} catch {
		return false;
	}
}

function readSessionStorage(): Storage | null {
	if (typeof window === "undefined") return null;
	try {
		return window.sessionStorage;
	} catch {
		return null;
	}
}

function readPersistentStorage(): Storage | null {
	if (typeof window === "undefined") return null;
	try {
		return window.localStorage;
	} catch {
		return null;
	}
}

function createAuthRequestId(): string {
	try {
		const randomId = window.crypto?.randomUUID?.();
		if (randomId) return randomId;
	} catch {
		/* crypto may be unavailable in embedded storefront contexts */
	}
	return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
}

async function readAuthStatus(statusUrl: URL): Promise<PopupAuthMessage | null> {
	const response = await fetch(statusUrl.toString(), {
		method: "GET",
		credentials: "omit",
		cache: "no-store",
	});
	if (!response.ok) return null;
	const data = await response.json() as PopupAuthMessage & { status?: string };
	return data.status === "done" ? data : null;
}

export function getStoredProfileSession(): ProfileAuthSession | null {
	try {
		const storage = readSessionStorage();
		const persistentStorage = readPersistentStorage();
		const raw = storage?.getItem(SESSION_KEY) || persistentStorage?.getItem(SESSION_KEY);
		if (!raw) return null;
		const parsed = JSON.parse(raw) as ProfileAuthSession;
		if (!parsed?.accessToken) return null;
		try {
			storage?.setItem(SESSION_KEY, JSON.stringify(parsed));
		} catch {
			/* sessionStorage may be blocked by the host page */
		}
		return parsed;
	} catch {
		return null;
	}
}

export function setStoredProfileSession(session: ProfileAuthSession): void {
	const storage = readSessionStorage();
	const persistentStorage = readPersistentStorage();
	if (!storage && !persistentStorage) return;
	try {
		const payload = JSON.stringify(session);
		storage?.setItem(SESSION_KEY, payload);
		persistentStorage?.setItem(SESSION_KEY, payload);
		emitProfileAuthChange("profile-auth-set");
	} catch {
		/* browser storage may be blocked by the host page */
	}
}

export function clearStoredProfileSession(): void {
	try {
		readSessionStorage()?.removeItem(SESSION_KEY);
		readPersistentStorage()?.removeItem(SESSION_KEY);
		emitProfileAuthChange("profile-auth-clear");
	} catch {
		/* browser storage may be blocked by the host page */
	}
}

export function startSocialProfileLogin(provider: SocialProvider, apiUrl?: string): Promise<ProfileAuthSession> {
	if (typeof window === "undefined") {
		return Promise.reject(new Error("Social login must run in the browser."));
	}

	const baseUrl = getProfileAuthApiUrl(apiUrl);
	const backendOrigin = new URL(baseUrl).origin;
	const authRequestId = createAuthRequestId();
	const startUrl = new URL(`${baseUrl.replace(/\/$/, "")}/api/sdk/v1/auth/${provider}/start`);
	startUrl.searchParams.set("origin", window.location.origin);
	startUrl.searchParams.set("requestId", authRequestId);

	const statusUrl = new URL(`${baseUrl.replace(/\/$/, "")}/api/sdk/v1/auth/${provider}/status`);
	statusUrl.searchParams.set("origin", window.location.origin);
	statusUrl.searchParams.set("requestId", authRequestId);

	const popup = window.open(
		startUrl.toString(),
		"primestyle-profile-login",
		"width=520,height=720"
	);

	if (!popup) {
		return Promise.reject(new Error("Popup was blocked. Please allow popups and try again."));
	}

	return new Promise((resolve, reject) => {
		let settled = false;

		const cleanup = () => {
			window.removeEventListener("message", onMessage);
			window.clearInterval(statusTimer);
			window.clearTimeout(timeout);
		};

		const finish = (fn: () => void) => {
			if (settled) return;
			settled = true;
			cleanup();
			fn();
		};

		const onMessage = (event: MessageEvent<PopupAuthMessage>) => {
			if (!isTrustedAuthOrigin(event.origin, backendOrigin)) return;
			const data = event.data;
			if (!data || data.type !== AUTH_MESSAGE_TYPE) return;

			if (!data.ok || !data.accessToken) {
				finish(() => reject(new Error(data.error || "Social login failed.")));
				return;
			}

			finish(() => resolve({
				accessToken: data.accessToken as string,
				isNewUser: Boolean(data.isNewUser),
				signedInAt: Date.now(),
			}));
		};

		const handleAuthPayload = (data: PopupAuthMessage) => {
			if (!data.ok || !data.accessToken) {
				finish(() => reject(new Error(data.error || "Social login failed.")));
				return;
			}

			finish(() => resolve({
				accessToken: data.accessToken as string,
				isNewUser: Boolean(data.isNewUser),
				signedInAt: Date.now(),
			}));
		};

		const statusTimer = window.setInterval(() => {
			readAuthStatus(statusUrl)
				.then((data) => {
					if (data) handleAuthPayload(data);
				})
				.catch(() => {
					/* The popup message path can still finish the login. */
				});
		}, POLL_INTERVAL_MS);

		const timeout = window.setTimeout(() => {
			finish(() => reject(new Error("Login did not finish. Please close the sign-in window and try again.")));
		}, POPUP_TIMEOUT_MS);

		window.addEventListener("message", onMessage);
	});
}

export function getProfileAuthApiUrl(apiUrl?: string): string {
	const baseUrl = getApiUrl(apiUrl);
	try {
		const parsed = new URL(baseUrl, typeof window !== "undefined" ? window.location.origin : "https://api.primestyleai.com");
		const path = parsed.pathname.replace(/\/+$/, "");
		if (path === "/apps/primestyle" || path.startsWith("/apps/primestyle/")) {
			return "https://api.primestyleai.com";
		}
		if (path === "/api/proxy" || path.startsWith("/api/proxy/")) {
			return parsed.hostname === "shopify-staging.primestyleai.com"
				? "https://test-be-9a7k.primestyleai.com"
				: "https://api.primestyleai.com";
		}
	} catch {
		/* fall back to the configured API URL */
	}
	return baseUrl;
}
